/**
 * A/B Test Configurations
 *
 * Each test should have:
 * - enabled: boolean (can be overridden by environment variables)
 * - variants: object with variant configurations
 *   - weight: percentage of traffic (should sum to 100)
 *   - path: optional path to redirect to
 *   - description: human-readable description
 * - cookieName: name of cookie to store variant
 * - cookieExpiry: number of days before cookie expires
 * - urlParam: optional query param name (e.g. "_vt") to read/set experiment id in URL
 */

export const AB_TESTS = {
  /**
   * Payment Flow Test
   * Tests Payment Intent (with Pay Later) vs Setup Intent (original)
   */
  payment_flow: {
    enabled: false,
    variants: {
      control: {
        maskedId: 'p3w7',
        weight: 50,
        path: null,
        description: 'Setup Intent (Original Flow)',
        features: {
          usePaymentIntent: false,
          payLaterEnabled: false
        }
      },
      payment_intent: {
        maskedId: 'k9m2',
        weight: 50,
        path: null,
        description: 'Payment Intent with Pay Later (Klarna, Affirm)',
        features: {
          usePaymentIntent: true,
          payLaterEnabled: true
        }
      }
    },
    cookieName: 'ab_payment_flow',
    cookieExpiry: 30,
    paths: ['/cv-1'],
    description: 'Test Payment Intent vs Setup Intent for checkout'
  },

  /**
   * Funnel Split – A/B test between two funnels
   * User lands on /cv-1 with ?affid=2; 50% stay on cv-1, 50% redirect to /cv-2.
   * Without affid=2 the split does not run (always cv-1).
   */
  funnel_split: {
    enabled: false,
    variants: {
      control: {
        maskedId: 'f5n9',
        weight: 50,
        path: null,
        description: 'Default funnel (cv-1)'
      },
      funnel_alt: {
        maskedId: 'a5r7',
        weight: 50,
        path: '/cv-2',
        description: 'Alternate funnel (cv-2)'
      }
    },
    cookieName: 'ab_funnel_split_cv',
    cookieExpiry: 30,
    paths: ['/cv-1'], // Only run on landing path; redirect sends user to alternate funnel
    requiredQueryParams: { affid: '2' },
    description: '50/50 cv-1 vs cv-2 split only when URL contains affid=2'
  },

  /**
   * Component Experiment – same funnel, alternate components per variant
   * Experiment ID is passed as GET parameter (e.g. ?_vt=current_height_weight_new)
   * Use componentOverrides to divert traffic to alternate components for any step.
   * E.g. to test CurrentHeightWeightNew.jsx: add variant with
   * componentOverrides: { current_height_weight: 'CurrentHeightWeightNew' }
   * and import that component in FunnelRouter's componentMap.
   */
  checkout_experiment: {
    enabled: false, // DISABLED - Using checkout_experiment_v2 instead
    urlParam: '_vt',
    defaultVariant: 'checkout_v1',
    variants: {
      checkout_v1: {
        maskedId: 'c7x3',
        weight: 0,
        path: null,
        description: 'Default components (no overrides)',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPage'
        }
      },
      checkout_v2: {
        maskedId: 'h2q9',
        weight: 100,
        path: null,
        description: 'Alternate BmiCheckoutPage component',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV2'
        }
      },
    },
    cookieName: 'ab_checkout_experiment',
    cookieExpiry: 30,
    paths: ['/', '/cv-1'],
    description: 'A/B test alternate components per step; experiment id in URL as ?_vt=<variant>'
  },

  /**
   * Checkout Experiment V2 – BNPL vs Non-BNPL (ACTIVE)
   * Tests checkout with BNPL payment options vs Card-only
   * V3 (checkout_v3): Card only (BNPL commented) - DEFAULT (matches previous checkout_v2 winner)
   * V4 (checkout_v4): Card + BNPL (Klarna, Afterpay)
   */
  checkout_experiment_v2: {
    enabled: false,
    urlParam: '_vt',
    defaultVariant: 'checkout_v3', // Uses BmiCheckoutPageV2 (Card Only) - matches previous 100% traffic
    variants: {
      checkout_v3: {
        maskedId: 'c2n8',
        weight: 100,
        path: null,
        description: 'Checkout V3 - Card Only (No BNPL)',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV2'
        }
      },
      checkout_v4: {
        maskedId: 'c3b4',
        weight: 0,
        path: null,
        description: 'Checkout V4 - Card + BNPL (Affirm, Klarna, Afterpay)',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV3'
        }
      },
    },
    cookieName: 'ab_checkout_experiment_v2',
    cookieExpiry: 30,
    paths: ['/', '/cv-1'],
    description: 'A/B test BNPL payment options: Card-only vs Card+BNPL'
  },

  /**
   * Checkout Experiment V3 - Semaglutide First vs Tirzepatide First
   * STOPPED: V6 (Tirzepatide First) failed. Disabled to revert all users to default (V5 Sema First).
   */
  checkout_experiment_v3: {
    enabled: false, // V6 Tirzepatide First failed - disabled, all users see default checkout (V5 Sema First)
    urlParam: '_vt',
    defaultVariant: 'checkout_v5',
    variants: {
      checkout_v5: {
        maskedId: 'c5v6',
        weight: 100,
        path: null,
        description: 'Checkout V5 - Semaglutide First',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV2'
        }
      },
      checkout_v6: {
        maskedId: 'c6v7',
        weight: 0,
        path: null,
        description: 'Checkout V6 - Tirzepatide First',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV4'
        }
      },
    },
    cookieName: 'ab_checkout_experiment_v3',
    cookieExpiry: 30,
    paths: ['/', '/cv-1'],
    description: 'A/B test Semaglutide First vs Tirzepatide First'
  },

  /**
   * Checkout Experiment V4 - Monthly Plan Position (ACTIVE)
   * Base: Semaglutide First (V5 winner) — testing plan card order
   * V7 (checkout_v7): Monthly Plan first (current order) — control
   * V8 (checkout_v8): Monthly Plan moved below bundle plans (Quarterly, 6-Month, Annual first)
   */
  checkout_experiment_v4: {
    enabled: false,
    urlParam: '_vt',
    defaultVariant: 'checkout_v7',
    variants: {
      checkout_v7: {
        maskedId: 'c7v8',
        weight: 0,
        path: null,
        description: 'Checkout V7 - Sema First, Monthly Plan First (control)',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV2'
        }
      },
      checkout_v8: {
        maskedId: 'c8v9',
        weight: 100,
        path: null,
        description: 'Checkout V8 - Sema First, Monthly Plan Below Bundle Plans',
        componentOverrides: {
          bmi_checkout_page: 'BmiCheckoutPageV5'
        }
      },
    },
    cookieName: 'ab_checkout_experiment_v4',
    cookieExpiry: 30,
    paths: ['/', '/cv-1'],
    description: 'A/B test Monthly Plan position on Sema First checkout: first vs below bundle plans'
  },

  height_weight_experiment: {
    enabled: false,
    urlParam: '_vt',
    defaultVariant: 'height_weight_v1',
    variants: {
      height_weight_v1: {
        maskedId: 'w6t4',
        weight: 10,
        path: null,
        description: 'Default components (no overrides)',
        componentOverrides: {
          current_height_weight: 'CurrentHeightWeight'
        }
      },
      height_weight_v2: {
        maskedId: 'm8k1',
        weight: 90,
        path: null,
        description: 'Alternate CurrentHeightWeight component',
        componentOverrides: {
          current_height_weight: 'CurrentHeightWeightNew'
        }
      },
    },
    cookieName: 'ab_height_weight_experiment',
    cookieExpiry: 30,
    paths: ['/', '/cv-1'],
    description: 'A/B test alternate components per step; experiment id in URL as ?_vt=<variant>'
  },

  /**
   * Checkout Layout Test (Example - currently disabled)
   */
  checkout_layout: {
    enabled: false,
    variants: {
      control: {
        maskedId: 'l9p2',
        weight: 70,
        path: null,
        description: 'Original checkout layout'
      },
      variant_a: {
        maskedId: 's5j7',
        weight: 30,
        path: null,
        description: 'Streamlined checkout'
      }
    },
    cookieName: 'ab_checkout_layout',
    cookieExpiry: 30,
    paths: ['/', '/cv-1', '/funnel3'],
    description: 'Test checkout page layouts'
  }
}

/**
 * Get test configuration
 * @returns {Object} Configured tests
 */
export function getTestConfig() {
  return AB_TESTS
}

/**
 * Find which test contains a given variant name
 * @param {string} variantName - Variant name to search for (e.g. 'checkout_v1', 'height_weight_v2')
 * @returns {Object|null} Object with testName, variantName, and testConfig, or null if not found
 */
export function findTestByVariantName(variantName) {
  for (const [testName, testConfig] of Object.entries(AB_TESTS)) {
    if (!testConfig.variants) continue
    if (testConfig.variants[variantName]) {
      return { 
        testName, 
        variantName,
        testConfig 
      }
    }
  }
  return null
}

/**
 * Find which test contains a given masked ID
 * @param {string} maskedId - Masked ID to search for (e.g. 'h2q9', 'm8k1')
 * @returns {Object|null} Object with testName, variantKey, and testConfig, or null if not found
 */
export function findTestByMaskedId(maskedId) {
  for (const [testName, testConfig] of Object.entries(AB_TESTS)) {
    if (!testConfig.variants) continue
    for (const [variantKey, variantConfig] of Object.entries(testConfig.variants)) {
      if (variantConfig.maskedId === maskedId) {
        return { 
          testName, 
          variantKey,
          testConfig 
        }
      }
    }
  }
  return null
}

/**
 * Get masked ID for a given variant name
 * @param {string} variantName - Variant name (e.g. 'checkout_v1', 'height_weight_v2')
 * @returns {string} Masked ID if found, or original variantName as fallback
 */
export function getMaskedId(variantName) {
  for (const test of Object.values(AB_TESTS)) {
    if (!test.variants) continue
    if (test.variants[variantName]) {
      return test.variants[variantName].maskedId || variantName
    }
  }
  return variantName
}

/**
 * Unmask a masked ID to get the actual variant name
 * @param {string} maskedId - Masked ID (e.g. 'h2q9', 'm8k1')
 * @returns {string} Variant name if found, or original maskedId as fallback
 */
export function unmaskVariantId(maskedId) {
  const result = findTestByMaskedId(maskedId)
  return result ? result.variantKey : maskedId
}

/**
 * Extract component overrides for client-side rendering.
 * Maps variant name -> { stepId -> componentName } for all tests that define componentOverrides.
 * @returns {Object<string, Object<string, string>>}
 */
export function getClientComponentOverrides() {
  const overrides = {}
  for (const test of Object.values(AB_TESTS)) {
    if (!test.variants) continue
    for (const [variantId, variantConfig] of Object.entries(test.variants)) {
      if (variantConfig.componentOverrides && Object.keys(variantConfig.componentOverrides).length > 0) {
        overrides[variantId] = variantConfig.componentOverrides
      }
    }
  }
  return overrides
}

/**
 * Check if a path should be tested
 * @param {string} pathname - URL pathname
 * @param {Array} testPaths - Array of paths to test
 * @returns {boolean}
 */
export function shouldTestPath(pathname, testPaths) {
  if (!testPaths || testPaths.length === 0) {
    return true
  }

  return testPaths.some(path => {
    if (path.endsWith('*')) {
      return pathname.startsWith(path.slice(0, -1))
    }
    return pathname === path || pathname.startsWith(path + '/')
  })
}

/**
 * Check if the request URL matches required query params for a test.
 * Missing or mismatched params skip the test (e.g. funnel_split only when affid=2).
 * @param {URL|string} url - Request URL
 * @param {Object} requiredQueryParams - Map of query param name -> expected value
 * @returns {boolean}
 */
export function matchesRequiredQueryParams(url, requiredQueryParams) {
  if (!requiredQueryParams || Object.keys(requiredQueryParams).length === 0) {
    return true
  }

  const searchParams = url instanceof URL
    ? url.searchParams
    : new URL(url, 'http://localhost').searchParams

  return Object.entries(requiredQueryParams).every(([key, expected]) => {
    return searchParams.get(key) === String(expected)
  })
}
